import { Injectable } from '@angular/core';
import { EventName } from '@app/core/models/event-bus.models';
import { ApiService } from '@app/core/services/api.service';
import { EventBusService } from '@app/core/services/event-bus.service';
import { SnackbarService } from '@app/core/services/snack-bar.service';
import { WebSocketService } from '@app/core/services/websocket.service';
import { TranslateService } from '@ngx-translate/core';
import { Meeting, MeetingJoinResponse, WSMeeting } from '@shared/models/meeting.models';
import { BehaviorSubject, catchError, firstValueFrom, map, Observable, of, tap } from 'rxjs';

import { MeetingsStorageService } from './meetings-storage.service';

@Injectable({
  providedIn: 'root',
})
export class MeetingsService extends ApiService {
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly errorSubject = new BehaviorSubject<boolean>(false);
  private readonly selectedMeetingSubject = new BehaviorSubject<Meeting | null>(null);

  public readonly data$ = this.meetingStorageService.allMeetings$;
  public readonly loading$ = this.loadingSubject.asObservable();
  public readonly hasError$ = this.errorSubject.asObservable();
  public readonly selectedMeeting$ = this.selectedMeetingSubject.asObservable();

  private readonly initialLoad: Promise<void>;

  constructor(
    private webSocketService: WebSocketService,
    private snackbar: SnackbarService,
    private meetingStorageService: MeetingsStorageService,
    private translateService: TranslateService,
    private eventBusService: EventBusService
  ) {
    super('users/{me}/gatherings');

    this.initialLoad = this.meetingStorageService.workerGetAllMeetings().then((meetings) => {
      this.meetingStorageService.seedInMemory(meetings);
    });

    this.bindSocket();

    this.eventBusService.on(EventName.Logout).subscribe(() => {
      this.meetingStorageService.drop();
    });
  }

  public init(): void | Error {
    // Initialize the service
    // Note: Data fetching is done separately via refreshData()
  }

  public refreshData(): Observable<void> {
    this.loadingSubject.next(true);
    this.errorSubject.next(false);

    return this.getAllMeetings().pipe(
      tap(async (meetings) => {
        await this.initialLoad;
        this.meetingStorageService.replaceWith(meetings);
        this.loadingSubject.next(false);
      }),
      catchError((error) => {
        console.error('[MEETING-SERVICE] Error refreshing data:', error);
        this.loadingSubject.next(false);
        this.errorSubject.next(true);
        return of(void 0);
      }),
      map(() => void 0)
    );
  }

  public async createMeeting(meeting: Partial<Meeting>): Promise<Meeting | null> {
    try {
      const createdMeeting = await firstValueFrom(this.post<Meeting>('', meeting));
      this.meetingStorageService.addMeetingItem(createdMeeting.id, createdMeeting);
      return createdMeeting;
    } catch (error) {
      console.error('[MEETING-SERVICE] Error creating meeting:', error);
      this.snackbar.open(this.translateService.instant('MEETINGS.ERROR_CREATE'));
      return null;
    }
  }

  public async updateMeeting(id: string, meeting: Partial<Meeting>): Promise<Meeting | null> {
    try {
      const updatedMeeting = await firstValueFrom(this.put<Meeting>(id, meeting));
      this.meetingStorageService.updateMeetingItem(id, updatedMeeting);
      return updatedMeeting;
    } catch (error) {
      console.error('[MEETING-SERVICE] Error updating meeting:', error);
      return null;
    }
  }

  public async deleteMeeting(id: string): Promise<boolean> {
    try {
      await firstValueFrom(this.delete(id));
      this.meetingStorageService.removeMeetingItem(id);
      return true;
    } catch (error) {
      console.error('[MEETING-SERVICE] Error deleting meeting:', error);
      return false;
    }
  }

  public selectMeeting(meeting: Meeting | null): void {
    this.selectedMeetingSubject.next(meeting);
  }

  public async fetchMeetingCredentials(id: string): Promise<MeetingJoinResponse | null> {
    try {
      const joinResponse = await firstValueFrom(this.post<MeetingJoinResponse>(`${id}/join`, {}));
      return joinResponse;
    } catch (error) {
      console.error('[MEETING-SERVICE] Error fetching meeting credentials:', error);
      this.snackbar.open(this.translateService.instant('MEETINGS.ERROR_JOINING_MEETING'));
      return null;
    }
  }

  public async copyMeetingUrl(meeting: Meeting): Promise<void> {
    if (meeting.meetingUrl) {
      try {
        await navigator.clipboard.writeText(meeting.meetingUrl);
        this.snackbar.open(this.translateService.instant('MEETINGS.URL_COPIED'));
      } catch (error) {
        console.error('[MEETING-SERVICE] Error copying meeting URL:', error);
        this.snackbar.open(this.translateService.instant('MEETINGS.ERROR_COPY_URL'));
      }
    }
  }

  public async copyMeetingInvitation(meeting: Meeting): Promise<void> {
    const invitation = this.generateInvitationText(meeting);
    try {
      await navigator.clipboard.writeText(invitation);
      this.snackbar.open(this.translateService.instant('MEETINGS.INVITATION_COPIED'));
    } catch (error) {
      console.error('[MEETING-SERVICE] Error copying invitation:', error);
      this.snackbar.open(this.translateService.instant('MEETINGS.ERROR_COPY_INVITATION'));
    }
  }

  /**
   * Opens a new Outlook web calendar event in a new tab, pre-filled with the meeting
   * invitation in the event body. The date/time is intentionally left blank so the
   * user can pick it themselves (these meetings are persistent rooms with no schedule).
   */
  public addToOutlookCalendar(meeting: Meeting): void {
    // Outlook renders the body as HTML, so convert plain-text line breaks to <br>.
    const body = this.generateCalendarBodyText(meeting);

    const params = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      body,
    });

    const outlookUrl = `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`;
    window.open(outlookUrl, '_blank', 'noopener,noreferrer');
  }

  private generateCalendarBodyText(meeting: Meeting): string {
    const meetingName = meeting.personal ? this.translateService.instant('MEETINGS.MY_PERSONAL_ROOM') : meeting.name;
    let invitation = `${meetingName}<br><br>`;
    invitation += `${this.translateService.instant('MEETINGS.MEETING_ID')}: ${meeting.meetingId}<br>`;

    if (meeting.meetingCode) {
      invitation += `${this.translateService.instant('MEETINGS.MEETING_CODE')}: ${meeting.meetingCode}<br>`;
    }

    if (meeting.pinCode) {
      invitation += `${this.translateService.instant('MEETINGS.PIN_CODE')}: ${meeting.pinCode}<br>`;
    }

    if (meeting.meetingUrl) {
      invitation += `${this.translateService.instant('MEETINGS.MEETING_URL')}: ${meeting.meetingUrl}<br>`;
    }

    if (meeting.dialInNumbers && meeting.dialInNumbers.length > 0) {
      invitation += `<br>${this.translateService.instant('MEETINGS.DIAL_IN_NUMBERS')}:<br>`;
      meeting.dialInNumbers.forEach((number) => {
        invitation += `${number}<br>`;
      });
    }

    return invitation;
  }

  public async canDeleteMeeting(meetingId: string, userId: string): Promise<boolean> {
    try {
      const meetings = await firstValueFrom(this.data$);
      const meeting = meetings.find((m) => m.id === meetingId);
      return meeting ? meeting.ownerId === userId : false;
    } catch (error) {
      console.error('[MEETING-SERVICE] Error checking delete permission:', error);
      return false;
    }
  }

  private generateInvitationText(meeting: Meeting): string {
    let invitation = `${this.translateService.instant('MEETINGS.INVITATION_TEMPLATE_JOIN', { meetingName: meeting.name })}\n\n`;
    invitation += `${this.translateService.instant('MEETINGS.MEETING_ID')}: ${meeting.meetingId}\n`;

    if (meeting.meetingCode) {
      invitation += `${this.translateService.instant('MEETINGS.MEETING_CODE')}: ${meeting.meetingCode}\n`;
    }

    if (meeting.pinCode) {
      invitation += `${this.translateService.instant('MEETINGS.PIN_CODE')}: ${meeting.pinCode}\n`;
    }

    if (meeting.meetingUrl) {
      invitation += `${this.translateService.instant('MEETINGS.MEETING_URL')}: ${meeting.meetingUrl}\n`;
    }

    if (meeting.dialInNumbers && meeting.dialInNumbers.length > 0) {
      invitation += `\n${this.translateService.instant('MEETINGS.DIAL_IN_NUMBERS')}:\n`;
      meeting.dialInNumbers.forEach((number) => {
        invitation += `${number}\n`;
      });
    }

    return invitation;
  }

  private getAllMeetings(): Observable<Meeting[]> {
    return this.get<Meeting[]>('', { params: { scope: 'all' } }).pipe(
      map((response) => response || []),
      catchError((error) => {
        console.error('[MEETING-SERVICE] Error fetching meetings:', error);
        return of([]);
      })
    );
  }

  private bindSocket(): void {
    this.webSocketService.on('Meeting', (data: WSMeeting) => {
      this.handleWebSocketMeeting(data);
    });

    // Handle Jitsi meeting creation events
    this.webSocketService.on('JitsiMeetingCreated', (data: Meeting) => {
      this.handleJitsiMeetingCreated(data);
    });

    // Handle Jitsi meeting update events
    this.webSocketService.on('JitsiMeetingUpdated', (data: Meeting) => {
      this.handleJitsiMeetingCreated(data);
    });

    // Handle Jitsi meeting deletion events
    this.webSocketService.on('JitsiMeetingDeleted', (data: { id: string }) => {
      this.handleJitsiMeetingDeleted(data);
    });

    // Handle Jitsi meeting participants update events
    this.webSocketService.on(
      'JitsiMeetingParticipantsUpdate',
      (data: { meetingId: string; participantCount: number; participants: string[] }) => {
        this.handleJitsiMeetingParticipantsUpdate(data);
      }
    );
  }

  private handleWebSocketMeeting(data: WSMeeting): void {
    const { action, model } = data;

    switch (action) {
      case 'create':
      case 'update':
        this.meetingStorageService.addMeetingItem(model.id, model);
        break;
      case 'delete':
        this.meetingStorageService.removeMeetingItem(model.id);
        break;
      default:
        console.warn('[MEETING-SERVICE] Unknown WebSocket action:', action);
    }
  }

  private handleJitsiMeetingCreated(meeting: Meeting): void {
    this.meetingStorageService.addMeetingItem(meeting.id, meeting);
    console.log('[MEETING-SERVICE] Jitsi meeting created:', meeting.id);
  }

  private handleJitsiMeetingDeleted(data: { id: string }): void {
    this.meetingStorageService.removeMeetingItem(data.id);
    console.log('[MEETING-SERVICE] Jitsi meeting deleted:', data.id);
  }

  private handleJitsiMeetingParticipantsUpdate(data: {
    meetingId: string;
    participantCount: number;
    participants: string[];
  }): void {
    // Fetch the existing meeting by meetingId (Jitsi ID), not by database id
    const existingMeeting = this.meetingStorageService.getByMeetingId(data.meetingId);

    if (!existingMeeting) {
      console.warn('[MEETING-SERVICE] Meeting not found for participants update:', data.meetingId);
      return;
    }

    // Update the meeting with the new participant count and participants list
    const updatedMeeting: Meeting = {
      ...existingMeeting,
      participants: {
        participantCount: data.participantCount,
        participants: data.participants ? [...data.participants] : [],
      },
    };

    // Use addMeetingItem with the database id (not meetingId)
    this.meetingStorageService.addMeetingItem(existingMeeting.id, updatedMeeting);
  }
}
